/**
 * feat-302 — PURE formatting of a stored audience object (design NOTES §4, §5).
 *
 * This file only DESCRIBES an audience in words. It never decides whether a
 * learner passes one — there is no TypeScript copy of the scope rule anywhere
 * (spec "View as", ADR 0001 §7). View as filters by the id sets the backend
 * returns, not by anything here.
 */
import {
  Audience,
  AudienceMode,
  AudienceOptions,
} from '../../../../services/quizContentAdminService';
import { AUDIENCE_COPY as COPY } from './audienceCopy';

/** True when both dimensions are `everyone`. */
export function isEveryone(a: Audience): boolean {
  return a.locations.mode === 'everyone' && a.roles.mode === 'everyone';
}

/**
 * Names for `ids`, in the order the OPTION LIST shows them (NOTES §4 — not the
 * order picked). `null` when any id cannot be named (the options did not load,
 * or name an id the list no longer has): the caller then uses the count form,
 * which is honest without a name.
 */
function namesInOptionOrder(
  ids: string[],
  options: Array<{ id: string; name: string }> | null,
): string[] | null {
  if (!options) return null;
  const wanted = new Set(ids);
  const named = options.filter((o) => wanted.has(o.id)).map((o) => o.name);
  return named.length === wanted.size ? named : null;
}

export function locationNames(ids: string[], options: AudienceOptions | null): string[] | null {
  return namesInOptionOrder(ids, options ? options.locations : null);
}

export function roleNames(keys: string[], options: AudienceOptions | null): string[] | null {
  return namesInOptionOrder(
    keys,
    options ? options.roles.map((r) => ({ id: r.role_key, name: r.display_name })) : null,
  );
}

function dimensionPart(
  mode: AudienceMode,
  names: string[] | null,
  count: number,
  countWord: (n: number) => string,
  max: number,
): string | null {
  if (mode === 'everyone') return null;
  const list = names && names.length <= max ? names.join(', ') : countWord(count);
  return mode === 'only' ? list : COPY.except(list);
}

/** The chip's two segments; `null` for a dimension that is `everyone`. */
export interface ChipSummary {
  location: string | null;
  role: string | null;
}

/**
 * NOTES §4. Names up to 2 when one dimension is scoped, up to 1 when both are;
 * otherwise the count form ("3 locations", "except 2 roles").
 */
export function chipSummary(a: Audience, options: AudienceOptions | null): ChipSummary {
  const both = a.locations.mode !== 'everyone' && a.roles.mode !== 'everyone';
  const max = both ? 1 : 2;
  return {
    location: dimensionPart(
      a.locations.mode,
      locationNames(a.locations.ids, options),
      a.locations.ids.length,
      COPY.locationsCount,
      max,
    ),
    role: dimensionPart(
      a.roles.mode,
      roleNames(a.roles.keys, options),
      a.roles.keys.length,
      COPY.rolesCount,
      max,
    ),
  };
}

/** The chip label as text: segments joined with ` · `, location first.
 *  Both-everyone → "Everyone". */
export function chipSummaryText(a: Audience, options: AudienceOptions | null): string {
  const s = chipSummary(a, options);
  const parts = [s.location, s.role].filter((p): p is string => !!p);
  return parts.length ? parts.join(' · ') : COPY.everyone;
}

/** One dimension's summary on its own, for the options-failed note:
 *  "Everyone", "Alameda, Oakland", "except 3 locations". */
export function dimensionSummary(
  a: Audience,
  dim: 'locations' | 'roles',
  options: AudienceOptions | null,
): string {
  if (dim === 'locations') {
    return (
      dimensionPart(
        a.locations.mode,
        locationNames(a.locations.ids, options),
        a.locations.ids.length,
        COPY.locationsCount,
        2,
      ) ?? COPY.everyone
    );
  }
  return (
    dimensionPart(
      a.roles.mode,
      roleNames(a.roles.keys, options),
      a.roles.keys.length,
      COPY.rolesCount,
      2,
    ) ?? COPY.everyone
  );
}

/** "Alameda", "Alameda or Oakland", "Alameda, Fremont or Oakland". */
export function orJoin(items: string[]): string {
  if (items.length <= 1) return items.join('');
  if (items.length === 2) return `${items[0]} or ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`;
}

/** `aria-label` (NOTES §4): "Who sees this Lesson: locations only Alameda;
 *  roles every role. Change". Unnamed ids fall back to the count form. */
export function chipAria(
  a: Audience,
  level: 'track' | 'lesson',
  options: AudienceOptions | null,
): string {
  const loc =
    a.locations.mode === 'everyone'
      ? COPY.ariaEveryLocation
      : `${a.locations.mode} ${
          locationNames(a.locations.ids, options)?.join(', ') ??
          COPY.locationsCount(a.locations.ids.length)
        }`;
  const role =
    a.roles.mode === 'everyone'
      ? COPY.ariaEveryRole
      : `${a.roles.mode} ${
          roleNames(a.roles.keys, options)?.join(', ') ?? COPY.rolesCount(a.roles.keys.length)
        }`;
  return COPY.chipAria(level, loc, role);
}

/** The audience minus "Employees " and the period: "at Alameda, with the
 *  Greeter role" — the shared core of the sentence and the Track note. */
function whoClause(a: Audience, options: AudienceOptions | null): { loc: string; role: string } {
  const locs =
    locationNames(a.locations.ids, options) ?? [COPY.locationsCount(a.locations.ids.length)];
  const roles = roleNames(a.roles.keys, options) ?? [COPY.rolesCount(a.roles.keys.length)];
  const loc =
    a.locations.mode === 'everyone'
      ? 'at every location'
      : a.locations.mode === 'only'
        ? `at ${orJoin(locs)}`
        : `everywhere except ${orJoin(locs)}`;
  const role =
    a.roles.mode === 'everyone'
      ? ''
      : a.roles.mode === 'only'
        ? `, with the ${orJoin(roles)} role`
        : `, except anyone with the ${orJoin(roles)} role`;
  return { loc, role };
}

/**
 * "Who will see it" (NOTES §5): "Every employee." / "Employees at Alameda." /
 * "Employees everywhere except Oakland." / "Employees at San Francisco, except
 * anyone with the Cashier role." An invalid draft (an empty Only/Except list)
 * → "—".
 */
export function previewSentence(a: Audience, options: AudienceOptions | null): string {
  if (invalidDimensions(a).any) return COPY.previewInvalid;
  if (isEveryone(a)) return 'Every employee.';
  const { loc, role } = whoClause(a, options);
  return `Employees ${loc}${role}.`;
}

/** The Track note's fragment: "every employee" / "employees at Alameda". */
export function audienceFragment(a: Audience, options: AudienceOptions | null): string {
  if (isEveryone(a)) return 'every employee';
  const { loc, role } = whoClause(a, options);
  return `employees ${loc}${role}`;
}

/** Which dimensions are in Only/Except with an empty list (Save disabled). */
export function invalidDimensions(a: Audience): {
  locations: boolean;
  roles: boolean;
  any: boolean;
} {
  const locations = a.locations.mode !== 'everyone' && a.locations.ids.length === 0;
  const roles = a.roles.mode !== 'everyone' && a.roles.keys.length === 0;
  return { locations, roles, any: locations || roles };
}

/** The inline reason above Save, or null when the draft is saveable. */
export function invalidReason(a: Audience): string | null {
  const inv = invalidDimensions(a);
  if (inv.locations && inv.roles) return COPY.invalidBoth;
  if (inv.locations) return COPY.invalidLocations;
  if (inv.roles) return COPY.invalidRoles;
  return null;
}

/**
 * The body actually sent (NOTES §6.1): a dimension in `everyone` sends an empty
 * list, whatever the draft kept for flipping back.
 */
export function wireAudience(a: Audience): Audience {
  return {
    locations: {
      mode: a.locations.mode,
      ids: a.locations.mode === 'everyone' ? [] : [...a.locations.ids],
    },
    roles: { mode: a.roles.mode, keys: a.roles.mode === 'everyone' ? [] : [...a.roles.keys] },
  };
}

/** Order-insensitive equality of what would be stored (NOTES §6.2: no change →
 *  no request). */
export function sameAudience(a: Audience, b: Audience): boolean {
  const x = wireAudience(a);
  const y = wireAudience(b);
  const same = (p: string[], q: string[]) =>
    p.length === q.length && [...p].sort().join('\u0000') === [...q].sort().join('\u0000');
  return (
    x.locations.mode === y.locations.mode &&
    x.roles.mode === y.roles.mode &&
    same(x.locations.ids, y.locations.ids) &&
    same(x.roles.keys, y.roles.keys)
  );
}
